<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAuthStore } from '@/stores/auth';
import { useNotificationsStore } from '@/stores/notifications';
import type { UserNotification } from '@/types';
import NotificationDropdown from './NotificationDropdown.vue';

const router = useRouter();
const { t } = useI18n();
const authStore = useAuthStore();
const notificationsStore = useNotificationsStore();

const isOpen = ref(false);
const rootRef = ref<HTMLElement | null>(null);

const unreadBadge = computed(() => {
  if (notificationsStore.unreadCount > 99) {
    return '99+';
  }

  return String(notificationsStore.unreadCount);
});

const closeDropdown = (): void => {
  isOpen.value = false;
};

const handleOutsideClick = (event: MouseEvent): void => {
  if (!isOpen.value || !rootRef.value) {
    return;
  }

  const target = event.target as Node | null;
  if (!target) {
    return;
  }

  if (!rootRef.value.contains(target)) {
    closeDropdown();
  }
};

const refreshRecent = async (): Promise<void> => {
  await Promise.all([
    notificationsStore.fetchNotifications(1, 10),
    notificationsStore.fetchUnreadCount(),
  ]);
};

const handleSelect = async (notification: UserNotification): Promise<void> => {
  if (notification.read_at === null) {
    await notificationsStore.markAsRead(notification.id);
  }

  closeDropdown();

  if (notification.link) {
    await router.push(notification.link);
  }
};

const toggleDropdown = async (): Promise<void> => {
  isOpen.value = !isOpen.value;
  if (isOpen.value) {
    await refreshRecent();
  }
};

const handleMarkAllAsRead = async (): Promise<void> => {
  await notificationsStore.markAllAsRead();
  await notificationsStore.fetchUnreadCount();
};

const handleClearAll = async (): Promise<void> => {
  if (!window.confirm(t('notifications.confirmClear'))) {
    return;
  }

  await notificationsStore.clearAll();
  closeDropdown();
};

const handleViewAll = async (): Promise<void> => {
  closeDropdown();
  await router.push('/settings/notifications');
};

const handleVisibilityChange = (): void => {
  if (import.meta.env.SSR) return;
  if (document.hidden) {
    notificationsStore.pausePolling();
  } else {
    notificationsStore.resumePolling();
  }
};

const handleWindowFocus = (): void => {
  if (import.meta.env.SSR) return;
  void notificationsStore.fetchUnreadCount();
};

watch(
  () => authStore.isAuthenticated,
  async (isAuthenticated) => {
    if (import.meta.env.SSR) {
      return;
    }

    if (!isAuthenticated) {
      notificationsStore.reset();
      closeDropdown();
      return;
    }

    await refreshRecent();
    notificationsStore.startPolling();
  },
  { immediate: true }
);

onMounted(() => {
  document.addEventListener('mousedown', handleOutsideClick, true);
  document.addEventListener('visibilitychange', handleVisibilityChange);
  window.addEventListener('focus', handleWindowFocus);
});

onUnmounted(() => {
  document.removeEventListener('mousedown', handleOutsideClick, true);
  document.removeEventListener('visibilitychange', handleVisibilityChange);
  window.removeEventListener('focus', handleWindowFocus);
  notificationsStore.stopPolling();
});
</script>

<template>
  <div v-if="authStore.isAuthenticated" ref="rootRef" class="relative flex">
    <button
      type="button"
      class="relative inline-flex items-center justify-center p-2 text-cream-100 hover:text-gold-400 transition-colors"
      :aria-label="t('notifications.iconLabel')"
      @click="toggleDropdown"
    >
      <svg class="h-6 w-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden="true">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
      </svg>

      <span
        v-if="notificationsStore.unreadCount > 0"
        class="absolute -right-1 -top-1 inline-flex min-w-5 items-center justify-center rounded-full bg-burgundy-800 px-1.5 text-[10px] font-semibold text-white"
      >
        {{ unreadBadge }}
      </span>
    </button>

    <NotificationDropdown
      v-if="isOpen"
      :notifications="notificationsStore.recentItems"
      :loading="notificationsStore.loading"
      @select="handleSelect"
      @mark-all-read="handleMarkAllAsRead"
      @clear-all="handleClearAll"
      @view-all="handleViewAll"
      @refresh="refreshRecent"
    />
  </div>
</template>
